import { useMemo } from 'react'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import {
  ActivationSummary,
  ActivationProvider,
  ActivationProviderKey,
  ExternalIntegrationEnum,
} from 'api/clients/ExternalIntegrationsClient'
import { effectiveTacticsForPlan } from 'screens/blaze/Home/HomeV4/UpNext/rules/utils'
import { TACTIC_TO_PROVIDER } from 'screens/blaze/Home/VirtualMarketerHome/Brain/PlatformSection/Types'

const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  x: 'X / Twitter',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  word_press: 'WordPress',
  mailchimp: 'Mailchimp',
  google_business_profile: 'Google Business Profile',
}

// These providers must come through brand plan tactics — don't surface them just because
// the user happened to connect them outside their campaign setup.
const TACTIC_ONLY_PROVIDERS = new Set<string>([ExternalIntegrationEnum.WordPress, ExternalIntegrationEnum.Mailchimp])

export const useActivationProviders = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true
  const { data: brandPlan, isLoading: isBrandPlanLoading } = useCurrentBrandPlan()
  const { data: externalIntegrationsData, isLoading: isIntegrationsLoading } = useExternalIntegrations()

  const data = useMemo((): ActivationSummary | undefined => {
    if (!enabled || !brandPlan || !externalIntegrationsData) return undefined

    const tactics = effectiveTacticsForPlan(brandPlan)
    const externalProviders = externalIntegrationsData.external_integrations.map((i) => i.provider)
    const connectedSet = new Set(externalProviders)

    // Tactic-based providers — drives the primary list from the brand plan
    const tacticProvidersSeen = new Set<string>()
    const tacticProviders: ExternalIntegrationEnum[] = []
    for (const { tactic, provider } of TACTIC_TO_PROVIDER) {
      if (!tactics.includes(tactic) || tacticProvidersSeen.has(provider)) continue
      tacticProvidersSeen.add(provider)
      tacticProviders.push(provider)
    }

    // Connected integrations not covered by any tactic (e.g. TikTok connected but missing from tactics).
    // WordPress and Mailchimp are excluded — they must come from the campaign plan.
    const extraProviders = [
      ...new Set(externalProviders.filter((p) => !tacticProvidersSeen.has(p) && !TACTIC_ONLY_PROVIDERS.has(p))),
    ]

    const providers: ActivationProvider[] = [...tacticProviders, ...extraProviders].map((provider) => ({
      provider: provider as ActivationProviderKey,
      connected: connectedSet.has(provider),
      posts_waiting_count: 0,
      display_name: PROVIDER_DISPLAY_NAMES[provider] ?? provider,
      connect_strategy: 'oauth',
    }))

    return { providers }
  }, [enabled, brandPlan, externalIntegrationsData])

  const isLoading = enabled && (isBrandPlanLoading || isIntegrationsLoading)

  return { data, isLoading }
}
